import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import CryptoJS from 'crypto-js';
import { isSuperAdminAccount } from '@/lib/auth/is-super-admin';

// Types
export interface UserPermission {
  title: string;
  description: string;
  http_method: string;
  model_name: string;
}

interface UserData {
  id: number;
  email: string;
  first_name: string | null;
  last_name: string | null;
  user_code: string | null;
  account_type: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  is_deleted: boolean;
  admin?: {
    id: number;
    is_super_admin: boolean;
    created_at: string;
    updated_at: string;
    is_deleted: boolean;
    account_id: number;
    group_id: number;
  };
  user?: {
    id: number;
    user_type: string;
    created_at: string;
    updated_at: string;
    is_deleted: boolean;
    account_id: number;
    group_id: number;
    department?: {
      id: number;
      name: string;
    };
    section_leader?: unknown;
  };
}

// Auth State Interface
interface AuthState {
  // Auth data
  isAuthenticated: boolean;
  user: UserData | null;
  token: string | null;
  permissions: UserPermission[];
  isLoading: boolean;

  // Computed values
  role: string | null;
  userType: string | null;
  isSuperAdmin: boolean;
  groupId: number | null;
  groupName: string | null;
}

// Auth Actions Interface
interface AuthActions {
  // Core actions
  setAuth: (data: {
    user: UserData;
    token: string;
    permissions: UserPermission[];
    groupName?: string;
  }) => void;
  clearAuth: () => void;
  setLoading: (_loading: boolean) => void;
  updateUser: (_userData: Partial<UserData>) => void;

  // Permission helpers
  hasPermission: (_httpMethod: string, _model: string) => boolean;
  hasAnyPermission: (
    _permissions: { httpMethod: string; modelName: string }[]
  ) => boolean;
  hasAllPermissions: (
    _permissions: { httpMethod: string; modelName: string }[]
  ) => boolean;
  getModelPermissions: (_model: string) => UserPermission[];
}

type AuthStore = AuthState & AuthActions;

// Encryption for persistence
const SECRET_KEY = process.env.NEXT_PUBLIC_STORAGE_KEY || 'fallback-secret-key';

const encryptStorage = {
  getItem: (name: string) => {
    const item = localStorage.getItem(name);
    if (!item) return null;

    try {
      const bytes = CryptoJS.AES.decrypt(item, SECRET_KEY);
      return JSON.parse(bytes.toString(CryptoJS.enc.Utf8));
    } catch {
      return null;
    }
  },
  setItem: (name: string, value: any) => {
    try {
      const encrypted = CryptoJS.AES.encrypt(
        JSON.stringify(value),
        SECRET_KEY
      ).toString();
      localStorage.setItem(name, encrypted);
    } catch {
      // Fallback to regular storage
      localStorage.setItem(name, JSON.stringify(value));
    }
  },
  removeItem: (name: string) => localStorage.removeItem(name)
};

// Create the store
export const useAuthStore = create<AuthStore>()(
  devtools(
    persist(
      immer((set, get) => ({
        // Initial state
        isAuthenticated: false,
        user: null,
        token: null,
        permissions: [],
        isLoading: false,
        role: null,
        userType: null,
        isSuperAdmin: false,
        groupId: null,
        groupName: null,

        // Actions
        setAuth: (data) => {
          set((state) => {
            state.isAuthenticated = true;
            state.user = data.user;
            state.token = data.token;
            state.permissions = data.permissions;
            state.groupName = data.groupName || null;
            state.isLoading = false;

            // Set computed values
            state.role = data.user.account_type;
            state.userType = data.user.user?.user_type || null;
            state.isSuperAdmin = isSuperAdminAccount(data.user);
            state.groupId =
              data.user.admin?.group_id || data.user.user?.group_id || null;
          });
        },

        clearAuth: () => {
          set((state) => {
            state.isAuthenticated = false;
            state.user = null;
            state.token = null;
            state.permissions = [];
            state.role = null;
            state.userType = null;
            state.isSuperAdmin = false;
            state.groupId = null;
            state.groupName = null;
            state.isLoading = false;
          });
        },

        setLoading: (loading) => {
          set((state) => {
            state.isLoading = loading;
          });
        },

        updateUser: (userData) => {
          set((state) => {
            if (state.user) {
              Object.assign(state.user, userData);
            }
          });
        },

        // Permission helpers
        hasPermission: (httpMethod, model) => {
          const state = get();
          if (state.isSuperAdmin) return true;

          return state.permissions.some(
            (permission) =>
              permission.http_method === httpMethod &&
              permission.model_name === model
          );
        },

        hasAnyPermission: (permissions) => {
          const state = get();
          if (state.isSuperAdmin) return true;

          return permissions.some(({ httpMethod, modelName }) =>
            get().hasPermission(httpMethod, modelName)
          );
        },

        hasAllPermissions: (permissions) => {
          const state = get();
          if (state.isSuperAdmin) return true;

          return permissions.every(({ httpMethod, modelName }) =>
            get().hasPermission(httpMethod, modelName)
          );
        },

        getModelPermissions: (model) => {
          const state = get();
          return state.permissions.filter(
            (permission) => permission.model_name === model
          );
        }
      })),
      {
        name: 'auth-storage',
        storage: encryptStorage,
        // Only persist essential data, not loading states
        partialize: (state) => ({
          isAuthenticated: state.isAuthenticated,
          user: state.user,
          token: state.token,
          permissions: state.permissions,
          role: state.role,
          userType: state.userType,
          isSuperAdmin: state.isSuperAdmin,
          groupId: state.groupId,
          groupName: state.groupName
        })
      }
    ),
    { name: 'AuthStore' }
  )
);

// Separate auth API functions
export const authAPI = {
  async login(email: string, password: string) {
    const response = await fetch('/api/auth', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ email, password })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Login failed');
    }

    return response.json();
  },

  async checkAuth() {
    const response = await fetch('/api/auth', {
      method: 'GET',
      credentials: 'include',
      cache: 'no-store'
    });

    if (!response.ok) {
      throw new Error('Authentication check failed');
    }

    return response.json();
  },

  async logout() {
    await fetch('/api/auth', {
      method: 'DELETE',
      credentials: 'include'
    });
  }
};

// Auth hooks
export const useAuth = () => {
  const store = useAuthStore();

  const login = async (email: string, password: string) => {
    store.setLoading(true);
    try {
      const data = await authAPI.login(email, password);

      const adminPermissions = data.adminPermissions;
      const userPermissions = data.userPermissions;
      const isAdmin = data.account.account_type === 'admin';
      const permissions = isAdmin
        ? adminPermissions?.permissions || []
        : userPermissions?.permissions || [];
      const groupInfo = isAdmin ? adminPermissions : userPermissions;

      store.setAuth({
        user: data.account,
        token: 'stored-in-httponly-cookie', // We don't store actual token in client
        permissions,
        groupName: groupInfo?.group_name
      });
    } catch (error) {
      store.clearAuth();
      throw error;
    }
  };

  const logout = async () => {
    store.setLoading(true);
    try {
      await authAPI.logout();
    } finally {
      store.clearAuth();
    }
  };

  const checkAuth = async () => {
    store.setLoading(true);
    try {
      const data = await authAPI.checkAuth();

      const account = data.user || data.account;
      const adminPermissions = data.adminPermissions;
      const userPermissions = data.userPermissions;
      const isAdmin = account?.account_type === 'admin';
      const permissions = isAdmin
        ? adminPermissions?.permissions || []
        : userPermissions?.permissions || [];
      const groupInfo = isAdmin ? adminPermissions : userPermissions;

      store.setAuth({
        user: account,
        token: 'stored-in-httponly-cookie',
        permissions,
        groupName: groupInfo?.group_name
      });
    } catch {
      store.clearAuth();
    }
  };

  return {
    ...store,
    login,
    logout,
    checkAuth
  };
};
